import { ActivityIndicator, Modal, Pressable, StyleSheet, Text, View } from 'react-native';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useTheme } from '@/contexts/ThemeContext';
import type { UpdateStatus } from '@/hooks/useUpdateCheck';

interface Props {
  status: UpdateStatus;
  onDismiss: () => void;
}

export function UpdatePrompt({ status, onDismiss }: Props) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const [applying, setApplying] = useState(false);

  if (status.type === 'none') return null;

  const forced = status.type === 'forced';
  const isOta = status.type === 'ota';
  const dismissible = !forced && !isOta;

  const startUpdate = status.startUpdate;
  const handleStart = () => {
    if (applying) return;
    setApplying(true);
    startUpdate();
  };

  return (
    <Modal transparent animationType="slide" statusBarTranslucent onRequestClose={() => { if (dismissible) onDismiss(); }}>
      <View style={styles.overlay}>
        <View style={[styles.card, { backgroundColor: colors.surface, borderColor: colors.border, paddingBottom: 36 + insets.bottom }]}>
          <Text style={styles.emoji}>🚀</Text>

          <Text style={[styles.title, { color: colors.text }]}>
            {forced ? t('updatePrompt.title_forced') : isOta ? t('updatePrompt.title_ota') : t('updatePrompt.title_optional')}
          </Text>

          <Text style={[styles.body, { color: colors.textMuted }]}>
            {forced
              ? t('updatePrompt.body_forced')
              : isOta
                ? t('updatePrompt.body_ota')
                : t('updatePrompt.body_optional')}
          </Text>

          {status.type === 'ota' && !!status.changelog && (
            <Text style={[styles.changelog, { color: colors.text, borderColor: colors.border }]}>
              {status.changelog}
            </Text>
          )}

          <Pressable
            style={[styles.btnPrimary, { backgroundColor: colors.primary }, applying && styles.btnDisabled]}
            onPress={handleStart}
            disabled={applying}
          >
            {applying ? (
              <ActivityIndicator color={colors.background} />
            ) : (
              <Text style={[styles.btnPrimaryText, { color: colors.background }]}>
                {isOta ? t('updatePrompt.action_restart') : t('updatePrompt.action_updateNow')}
              </Text>
            )}
          </Pressable>

          {dismissible && !applying && (
            <Pressable style={styles.btnDismiss} onPress={onDismiss}>
              <Text style={[styles.btnDismissText, { color: colors.textMuted }]}>
                {t('updatePrompt.action_later')}
              </Text>
            </Pressable>
          )}
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(13,27,42,0.75)',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: 20,
  },
  card: {
    width: '100%',
    maxWidth: 480,
    borderRadius: 20,
    borderWidth: 1,
    padding: 28,
    paddingBottom: 36,
    alignItems: 'center',
    gap: 12,
  },
  emoji: {
    fontSize: 42,
    marginBottom: 4,
  },
  title: {
    fontSize: 20,
    fontWeight: '800',
    textAlign: 'center',
  },
  body: {
    fontSize: 14,
    lineHeight: 21,
    textAlign: 'center',
    marginBottom: 4,
  },
  changelog: {
    fontSize: 13,
    lineHeight: 19,
    textAlign: 'left',
    alignSelf: 'stretch',
    borderWidth: 1,
    borderRadius: 12,
    padding: 12,
    marginBottom: 4,
  },
  btnPrimary: {
    width: '100%',
    paddingVertical: 14,
    borderRadius: 50,
    alignItems: 'center',
    marginTop: 4,
  },
  btnPrimaryText: {
    fontSize: 16,
    fontWeight: '800',
  },
  btnDisabled: {
    opacity: 0.7,
  },
  btnDismiss: {
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  btnDismissText: {
    fontSize: 14,
    fontWeight: '600',
  },
});
